import { useCallback, useEffect, useState } from 'react';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { ApiError } from '../../lib/apiClient';
import { getErrorMessage } from '../../lib/errors';
import { formatTimestamp } from '../../lib/format';
import type { AdminCommunityTemplateRead, CommunityTemplateStatus } from '../../types/api';
import { CommunityDesignPreview } from '../templates/CommunityDesignPreview';
import { communityStatusTone } from '../templates/communityStatus';
import { NEUTRAL_PALETTE, TEXT_ROLES, isAiBox, upgradeZones } from '../templates/communityZones';
import {
  approveAdminCommunityTemplate,
  fetchAdminCommunityTemplate,
  fetchAdminCommunityTemplates,
  rejectAdminCommunityTemplate,
  type ReviewableTemplateStatus,
} from './adminApi';
import { usePagedList } from './usePagedList';

/** Mirrors the backend's `MAX_REVIEW_NOTE_LENGTH`. The author reads this note in the studio. */
const REVIEW_NOTE_MAX_LENGTH = 500;

const FILTERS: readonly { status: ReviewableTemplateStatus; label: string }[] = [
  { status: 'pending', label: 'Pending' },
  { status: 'approved', label: 'Approved' },
  { status: 'rejected', label: 'Rejected' },
];

/**
 * The operator's words for a status. The member-facing labels ("In review",
 * "Needs changes") describe the author's position; these describe the queue's.
 */
const OPERATOR_STATUS_LABEL: Readonly<Record<CommunityTemplateStatus, string>> = {
  draft: 'Draft',
  pending: 'Pending',
  approved: 'Approved',
  rejected: 'Rejected',
};

function templateId(template: AdminCommunityTemplateRead): string {
  return template.id;
}

/**
 * The time a row is filed under: when it was submitted while it waits, when it
 * was answered after that. The read model has no `submitted_at`; a pending
 * template cannot be edited, so its `updated_at` is the submit.
 */
function queueTime(template: AdminCommunityTemplateRead): string {
  if (template.status === 'pending') return template.updated_at;
  return template.reviewed_at ?? template.updated_at;
}

function businessLabel(template: AdminCommunityTemplateRead): string {
  return template.business_name || `Deleted business ${template.business_id.slice(0, 8)}`;
}

/** What went wrong with a review action, in the operator's terms. */
function actionError(caught: unknown): string {
  if (caught instanceof ApiError && caught.status === 422) {
    return `The note must be 1 to ${REVIEW_NOTE_MAX_LENGTH} characters.`;
  }
  return getErrorMessage(caught, 'That did not save. Try again.');
}

/** The note an author gets back, with a live count against the limit. */
function ReviewNoteField({
  label,
  value,
  isBusy,
  onChange,
}: {
  label: string;
  value: string;
  isBusy: boolean;
  onChange: (value: string) => void;
}) {
  const length = value.trim().length;
  return (
    <label className="field">
      <span className="field__label">{label}</span>
      <textarea
        className="field__input"
        rows={4}
        maxLength={REVIEW_NOTE_MAX_LENGTH}
        value={value}
        disabled={isBusy}
        onChange={(event) => onChange(event.target.value)}
      />
      <span className="template-review__count">
        {length} / {REVIEW_NOTE_MAX_LENGTH}
      </span>
    </label>
  );
}

/**
 * One template, as the reviewer has to judge it: the design with every box's
 * default text, what the author wrote about it, and above the actions the AI
 * instructions it would put into every using member's prompt.
 *
 * The row it opens from is shown straight away; a fresh read replaces it on
 * mount, because the design's signed URL only lives an hour.
 */
function TemplateReviewPanel({
  template,
  onChanged,
  onClose,
}: {
  template: AdminCommunityTemplateRead;
  onChanged: (updated: AdminCommunityTemplateRead) => void;
  onClose: () => void;
}) {
  const [note, setNote] = useState('');
  const [isBusy, setIsBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [isConfirmingUnpublish, setIsConfirmingUnpublish] = useState(false);

  const id = template.id;

  useEffect(() => {
    const controller = new AbortController();
    void (async () => {
      try {
        const fresh = await fetchAdminCommunityTemplate(id, controller.signal);
        if (!controller.signal.aborted) onChanged(fresh);
      } catch {
        // The row already on screen is enough to review from; an expired
        // design URL is the only cost, and closing and reopening retries.
      }
    })();
    return () => controller.abort();
  }, [id, onChanged]);

  /** Someone else answered first: show what the template is now, and say so. */
  async function reloadAfterConflict(previous: CommunityTemplateStatus) {
    try {
      const fresh = await fetchAdminCommunityTemplate(id);
      onChanged(fresh);
      setNotice(
        fresh.status === previous
          ? 'That action no longer applies to this template. The details below are current.'
          : `This template is now ${OPERATOR_STATUS_LABEL[fresh.status].toLowerCase()}. Its status changed before your action landed, so nothing was saved. The details below are current.`,
      );
    } catch (caught) {
      setError(getErrorMessage(caught, 'Its status changed and it could not be reloaded.'));
    }
  }

  function run(action: () => Promise<AdminCommunityTemplateRead>) {
    if (isBusy) return;
    const previous = template.status;
    setIsBusy(true);
    setError(null);
    setNotice(null);

    void (async () => {
      try {
        const updated = await action();
        setIsBusy(false);
        setIsConfirmingUnpublish(false);
        setNote('');
        onChanged(updated);
      } catch (caught) {
        setIsBusy(false);
        setIsConfirmingUnpublish(false);
        if (caught instanceof ApiError && caught.code === 'TEMPLATE_NOT_REVIEWABLE') {
          await reloadAfterConflict(previous);
          return;
        }
        setError(actionError(caught));
      }
    })();
  }

  const trimmedNote = note.trim();
  const hasNote = trimmedNote.length > 0;
  const design = upgradeZones(template.zones);

  return (
    <section className="admin-panel template-review" aria-label={`Review ${template.title}`}>
      <div className="admin-panel__head">
        <h3 className="admin-panel__title">{template.title}</h3>
        <button className="button button--ghost" type="button" onClick={onClose}>
          Close
        </button>
      </div>

      {notice ? (
        <p className="form__notice" role="status">
          {notice}
        </p>
      ) : null}

      <div className="template-review__body">
        <CommunityDesignPreview
          className="template-review__preview"
          zones={template.zones}
          imageUrl={template.image_url}
          palette={NEUTRAL_PALETTE}
          fallbackHeadline=""
          label={`Design of ${template.title}, with the author's default text`}
        />

        <dl className="template-review__facts">
          <dt>Status</dt>
          <dd>
            <span className={communityStatusTone(template.status)}>
              {OPERATOR_STATUS_LABEL[template.status]}
            </span>
          </dd>
          <dt>Business</dt>
          <dd>{businessLabel(template)}</dd>
          <dt>Description</dt>
          <dd className="template-review__description">
            {template.description.trim() || 'No description.'}
          </dd>
          <dt>Attested</dt>
          <dd className="template-review__mono">
            {template.attested_at ? formatTimestamp(template.attested_at) : 'Not recorded'}
          </dd>
          <dt>Uses</dt>
          <dd className="template-review__mono">{template.use_count}</dd>
          <dt>Created</dt>
          <dd className="template-review__mono">{formatTimestamp(template.created_at)}</dd>
          {template.reviewed_at ? (
            <>
              <dt>Reviewed</dt>
              <dd className="template-review__mono">
                {formatTimestamp(template.reviewed_at)}
                {template.reviewed_by_email ? ` by ${template.reviewed_by_email}` : ''}
              </dd>
            </>
          ) : null}
          {template.public_image_url ? (
            <>
              <dt>Public image</dt>
              <dd>
                <a href={template.public_image_url} target="_blank" rel="noreferrer">
                  Open
                </a>
              </dd>
            </>
          ) : null}
        </dl>
      </div>

      <div className="template-review__hints">
        <p className="flag-card__label">AI instructions</p>
        {template.ai_hints.length > 0 ? (
          <>
            <p className="template-review__hints-lede">
              These go into the prompt of every member who posts on this design.
            </p>
            <ul className="template-review__hint-list">
              {template.ai_hints.map((hint) => (
                <li key={hint.box_id} className="template-review__hint">
                  <span className="template-review__hint-label">{hint.label}</span>
                  <span className="template-review__hint-text">{hint.hint}</span>
                </li>
              ))}
            </ul>
          </>
        ) : (
          <p className="template-review__hints-none">No AI instructions on this design.</p>
        )}
      </div>

      <div className="template-review__boxes">
        <p className="flag-card__label">Text boxes</p>
        <div className="learning-scroll">
          <table className="admin-table">
            <thead>
              <tr>
                <th scope="col">Role</th>
                <th scope="col">Label</th>
                <th scope="col">Filled by</th>
                <th scope="col">Default</th>
              </tr>
            </thead>
            <tbody>
              {design.texts.map((text) => (
                <tr key={text.id}>
                  <td>{TEXT_ROLES[text.role].label}</td>
                  <td>{text.label}</td>
                  <td>{isAiBox(text) ? 'AI' : 'Member'}</td>
                  <td className="template-review__default">
                    {text.default.trim() || <span className="template-review__empty">Empty</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {template.status === 'pending' ? (
        <div className="template-review__actions">
          <div className="admin-panel__actions">
            <button
              className="button button--primary"
              type="button"
              disabled={isBusy}
              onClick={() => run(() => approveAdminCommunityTemplate(id))}
            >
              {isBusy ? 'Saving' : 'Approve'}
            </button>
          </div>
          <ReviewNoteField
            label="Reason for rejecting (the author sees this)"
            value={note}
            isBusy={isBusy}
            onChange={setNote}
          />
          <div className="admin-panel__actions">
            <button
              className="button"
              type="button"
              disabled={isBusy || !hasNote}
              onClick={() => run(() => rejectAdminCommunityTemplate(id, { note: trimmedNote }))}
            >
              Reject
            </button>
          </div>
        </div>
      ) : template.status === 'approved' ? (
        <div className="template-review__actions">
          <ReviewNoteField
            label="Reason for unpublishing (the author sees this)"
            value={note}
            isBusy={isBusy}
            onChange={setNote}
          />
          <div className="admin-panel__actions">
            <button
              className="button button--danger"
              type="button"
              disabled={isBusy || !hasNote}
              onClick={() => setIsConfirmingUnpublish(true)}
            >
              Unpublish
            </button>
          </div>
        </div>
      ) : template.status === 'rejected' ? (
        <div className="template-review__actions">
          <p className="flag-card__label">Note to the author</p>
          <p className="template-review__note">{template.review_note ?? 'No note recorded.'}</p>
        </div>
      ) : null}

      {error ? (
        <p className="form__error" role="alert">
          {error}
        </p>
      ) : null}

      {isConfirmingUnpublish ? (
        <ConfirmDialog
          title="Unpublish this template"
          message="It leaves the community gallery now and the author sees your note. Members who already posted on it keep those posts, but any re-render of them falls back to the classic layout."
          confirmLabel="Unpublish"
          busyLabel="Unpublishing"
          isBusy={isBusy}
          onConfirm={() => run(() => rejectAdminCommunityTemplate(id, { note: trimmedNote }))}
          onDismiss={() => setIsConfirmingUnpublish(false)}
        />
      ) : null}
    </section>
  );
}

/** One page of the queue for a single status. Remounted when the filter moves. */
function TemplateList({ status }: { status: ReviewableTemplateStatus }) {
  const fetchPage = useCallback(
    (page: { limit: number; offset: number }, signal: AbortSignal) =>
      fetchAdminCommunityTemplates(status, page, signal),
    [status],
  );
  const list = usePagedList(fetchPage, templateId);
  const [selected, setSelected] = useState<AdminCommunityTemplateRead | null>(null);
  const { remove, replace, retry } = list;

  const handleChanged = useCallback(
    (updated: AdminCommunityTemplateRead) => {
      setSelected(updated);
      // A template whose status moved no longer belongs under this filter. The
      // panel stays open on it so the operator sees the result of what they did.
      if (updated.status !== status) {
        remove(updated.id);
        // The page is short by one now, so re-read it rather than showing 19.
        retry();
        return;
      }
      replace(updated);
    },
    [remove, replace, retry, status],
  );

  const clear = useCallback(() => setSelected(null), []);

  return (
    <>
      {list.isLoading ? (
        <p className="kb-section__status" role="status">
          Loading templates
        </p>
      ) : list.items.length === 0 && !list.error ? (
        <p className="admin-empty">
          {status === 'pending'
            ? 'Nothing waiting. The queue is clear.'
            : `No ${OPERATOR_STATUS_LABEL[status].toLowerCase()} templates.`}
        </p>
      ) : (
        <div className="learning-scroll">
          <table className="admin-table">
            <thead>
              <tr>
                <th scope="col">Title</th>
                <th scope="col">Business</th>
                <th scope="col">Status</th>
                <th scope="col">{status === 'pending' ? 'Submitted' : 'Reviewed'}</th>
              </tr>
            </thead>
            <tbody>
              {list.items.map((template) => (
                <tr
                  key={template.id}
                  className={template.id === selected?.id ? 'admin-table__row--open' : undefined}
                >
                  <td>
                    <button
                      className="admin-table__open"
                      type="button"
                      onClick={() => setSelected(template)}
                    >
                      {template.title}
                    </button>
                  </td>
                  <td>{businessLabel(template)}</td>
                  <td>
                    <span className={communityStatusTone(template.status)}>
                      {OPERATOR_STATUS_LABEL[template.status]}
                    </span>
                  </td>
                  <td className="admin-table__number">{formatTimestamp(queueTime(template))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {list.error ? (
        <div className="admin-pane__foot">
          <p className="form__error" role="alert">
            {list.error}
          </p>
          <button className="button" type="button" onClick={list.retry}>
            Try again
          </button>
        </div>
      ) : list.hasMore ? (
        <div className="admin-pane__foot">
          <button
            className="button"
            type="button"
            onClick={list.loadMore}
            disabled={list.isLoadingMore}
          >
            {list.isLoadingMore ? 'Loading' : 'Load more'}
          </button>
        </div>
      ) : null}

      {selected ? (
        <TemplateReviewPanel
          key={selected.id}
          template={selected}
          onChanged={handleChanged}
          onClose={clear}
        />
      ) : null}
    </>
  );
}

/**
 * The community template review queue: designs members submitted for the
 * shared gallery, oldest first, so the first submitted is the first answered.
 *
 * Approving publishes the design to every member; rejecting and unpublishing
 * send it back with a note the author reads in the studio. The author can post
 * on their own design whatever the answer is. This queue only decides whether
 * everyone else can.
 */
export function AdminTemplateQueue() {
  const [status, setStatus] = useState<ReviewableTemplateStatus>('pending');

  return (
    <div className="admin-pane">
      <div className="tabs" role="tablist" aria-label="Template status">
        {FILTERS.map((filter) => (
          <button
            key={filter.status}
            className={filter.status === status ? 'tabs__tab tabs__tab--active' : 'tabs__tab'}
            type="button"
            role="tab"
            aria-selected={filter.status === status}
            onClick={() => setStatus(filter.status)}
          >
            {filter.label}
          </button>
        ))}
      </div>

      <TemplateList key={status} status={status} />
    </div>
  );
}
